// Globals
import { SelectProps } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { MouseEvent, ReactNode, useCallback } from 'react';

// Components
import { CloseIcon } from '@/icons';
import { LoadingOutlined } from '@ant-design/icons';
import EmptyDataThumbnail from '../EmptyDataThumbnail/EmptyDataThumbnail';
import PureIconButton from '../PureIconButton/PureIconButton';
import {
  StyledDownIcon,
  StyledDownWrapper,
  StyledPrefix,
  StyledSelect,
  StyledTag,
  StyledWrapper,
} from './SelectStyles';

export type TSelectOption<V> = {
  value: V;
  label: string | JSX.Element;
};

export type TSelectOptions<V> = Array<TSelectOption<V>>;

export interface ISelectProps<Value, OptionValue> extends SelectProps<Value, TSelectOption<OptionValue>> {
  options: TSelectOptions<OptionValue>;
  isMultiselect?: boolean;
  noResultMessage?: string;
  prefix?: ReactNode;
}

const Select = <Value, OptionValue>({
  isMultiselect,
  options,
  noResultMessage,
  value,
  suffixIcon,
  loading,
  prefix,
  id,
  size = 'middle',
  ...props
}: ISelectProps<Value, OptionValue>): JSX.Element => {
  const renderTag = useCallback(({ label, onClose }: CustomTagProps) => {
    const handleClose = (evt: MouseEvent) => {
      evt.preventDefault();
      evt.stopPropagation();
      onClose();
    };

    return (
      <StyledTag>
        <span>{label}</span>
        <PureIconButton onClick={handleClose}>
          <CloseIcon />
        </PureIconButton>
      </StyledTag>
    );
  }, []);

  const renderDropdown = useCallback((menu: ReactNode) => <StyledDownWrapper>{menu}</StyledDownWrapper>, []);

  const renderSuffixIcon = loading ? <LoadingOutlined /> : suffixIcon;

  const selectComponent = (
    <StyledSelect<Value, TSelectOption<OptionValue>>
      tagRender={renderTag}
      {...props}
      value={value}
      options={options}
      optionFilterProp="label"
      loading={loading}
      dropdownRender={renderDropdown}
      notFoundContent={<EmptyDataThumbnail message={noResultMessage} />}
      size={size}
      {...(isMultiselect ? { mode: 'multiple' } : {})}
      // Hack for disabling browser autocomplete
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      autoComplete="dontshow"
      suffixIcon={renderSuffixIcon ?? <StyledDownIcon />}
    />
  );

  return (
    <StyledWrapper id={id}>
      {!!prefix && <StyledPrefix>{prefix}</StyledPrefix>}

      {selectComponent}
    </StyledWrapper>
  );
};

export default Select;
